import {
  Stack,
  Title,
  P,
  TextArea,
  Card,
  useToast,
  useActions,
  Icon,
  useTheme,
  Accordion,
  H4
} from "@introist/react-foundation/v2";
import { DrawerHeader } from "../../foundation/templates/Drawer/DrawerHeader";
import { useMemo, useState } from "react";
import { useAgents } from "../hooks/use-agents";
import { agentsApi, UpdateAgentRequest } from "../api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UnsavedChanges } from "../../components/molecules";
import { NakedButton } from "../../foundation/blocks/NakedButton";
import { SelectorIcon } from "../../modules/employees/fields/EmployeeFieldSelector";
import { AgentChannelsList } from "../components/AgentChannelsList";
import { AgentKnowledgeSourcesList } from "../components/AgentKnowledgeSourcesList";
import { getAgentTypeEditor } from "./editors";

interface ConfiguredAgentEditorProps {
  agentId: string;
  onClose: () => void;
}

export const AgentEditor = ({ agentId, onClose }: ConfiguredAgentEditorProps) => {
  const { data: configuredAgents = [] } = useAgents();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { theme } = useTheme();

  const { onConfirmAction } = useActions();

  const configuredAgent = useMemo(
    () => configuredAgents.find(a => a.id === agentId),
    [configuredAgents, agentId]
  );

  // Track only the changes, not the full state
  const [changes, setChanges] = useState<Partial<UpdateAgentRequest>>({});

  // Check if there are any changes
  const hasChanges = useMemo(() => {
    return Object.keys(changes).length > 0;
  }, [changes]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!configuredAgent) throw new Error("Agent not found");
      return agentsApi.update(configuredAgent.id, changes);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ai-studio", "agents", "configured"] });
      setChanges({}); // Clear changes after successful save
      toast.success("Agent saved successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to save: ${error.message}`);
    }
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async () => {
      if (!configuredAgent) throw new Error("Agent not found");
      const newStatus = !configuredAgent.is_active;
      await agentsApi.update(configuredAgent.id, {
        is_active: newStatus
      });
      return newStatus;
    },
    onSuccess: async newStatus => {
      await queryClient.invalidateQueries({ queryKey: ["ai-studio", "agents", "configured"] });
      toast.success(`Agent ${newStatus ? "activated" : "deactivated"} successfully`);
    },
    onError: (error: any) => {
      toast.error(`Failed to update status: ${error.message}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!configuredAgent) throw new Error("Agent not found");
      return agentsApi.delete(configuredAgent.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ai-studio", "agents", "configured"] });
      toast.success("Agent configuration deleted successfully");
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to delete: ${error.message}`);
    }
  });

  const handleSave = async () => {
    await updateMutation.mutateAsync();
  };

  const handleDiscard = () => {
    setChanges({});
  };

  const handleToggleActive = async () => {
    await toggleActiveMutation.mutateAsync();
  };

  const handleDelete = onConfirmAction(
    async () => {
      await deleteMutation.mutateAsync();
    },
    {
      title: "Delete agent configuration",
      description: `Are you sure you want to delete this agent configuration? This action cannot be undone.`,
      confirmTitle: "Delete"
    }
  );

  const AgentTypeEditor = useMemo(() => {
    if (!configuredAgent) return null;
    return getAgentTypeEditor(configuredAgent.type);
  }, [configuredAgent]);

  if (!configuredAgent) {
    return (
      <Stack vertical style={{ padding: "var(--spacing-large)" }}>
        <DrawerHeader title="Agent Not Found" onClose={onClose} />
        <P>This agent configuration could not be found.</P>
      </Stack>
    );
  }

  return (
    <Stack vertical style={{ padding: "var(--spacing-xLarge)", gap: "var(--spacing-xLarge)" }}>
      <DrawerHeader
        title={configuredAgent.name}
        onClose={onClose}
        actions={[
          {
            key: "delete",
            title: "Delete",
            onClick: handleDelete,
            startAdornment: <Icon name="trash" color={theme.palette.danger.default} />,
            titleStyles: { color: "var(--palette-danger-default)" }
          }
        ]}
      />

      <Card>
        <Stack
          style={{
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          {configuredAgent.is_active ? (
            <SelectorIcon icon="cycle" />
          ) : (
            <SelectorIcon icon="pause" />
          )}
          <Stack style={{ flex: 1 }}>
            <Title variant="bold">{configuredAgent.is_active ? "Active" : "Inactive"}</Title>
          </Stack>
          <NakedButton onClickWithLoading={handleToggleActive} disabled={hasChanges}>
            <Title
              style={{
                color: hasChanges
                  ? "var(--palette-foreground-subdued)"
                  : "var(--palette-primary-default)"
              }}
            >
              {configuredAgent.is_active ? "Deactivate" : "Activate"}
            </Title>
          </NakedButton>
        </Stack>
      </Card>

      <H4>General</H4>

      <Stack vertical>
        <AgentChannelsList agentConfigId={configuredAgent.id} />

        <AgentKnowledgeSourcesList agentConfigId={configuredAgent.id} />

        {configuredAgent.type !== "hr-helpdesk" && (
          <Accordion
            title="Instructions"
            description="Customize behavior with specific instructions"
            defaultOpen={false}
            icon={<SelectorIcon icon="info" />}
            compact
            toggleOpenOnHeaderClick
          >
            <Stack vertical style={{ gap: "var(--spacing-small)" }}>
              <TextArea
                value={changes.instructions ?? configuredAgent.instructions ?? ""}
                onChange={value => setChanges(prev => ({ ...prev, instructions: value }))}
                placeholder="Enter custom instructions for this agent..."
                rows={10}
              />
            </Stack>
          </Accordion>
        )}
      </Stack>

      {AgentTypeEditor && (
        <>
          <H4>{AgentTypeEditor.title ?? "Details"}</H4>
          <AgentTypeEditor.Editor
            agent={configuredAgent}
            onChange={config => setChanges(prev => ({ ...prev, config }))}
          />
        </>
      )}

      <UnsavedChanges
        show={hasChanges}
        onSave={handleSave}
        onDiscard={handleDiscard}
        style={{
          position: "sticky",
          bottom: "1rem",
          width: "320px",
          left: "calc(50% - 160px)"
        }}
      />
    </Stack>
  );
};
